import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '~/Store';
import { useGetSessions } from './hooks/useGetSessions';
import { formatHumanDateTime } from '~/DateTime';
import TailwindLayout from '~/ERP/TailwindLayout';

export const ERPHomepage: React.FC<React.PropsWithChildren<unknown>> = () => {
	const [selected, setSelected] = useState<string>('');
	const userId = useSelector(({ accessToken }: ApplicationState) => accessToken.accessToken?.user.id);

	const client = useSelector(({ apiClient }: ApplicationState) => apiClient.client);

	const sessions = useGetSessions(client, userId ?? '');

	const unique = sessions
		.map((item) => item.erpPack.brandName)
		.filter((value, index, self) => self.indexOf(value) === index);

	return (
		<TailwindLayout>
			<div className='w-full h-full overflow-auto'>
				<div className='px-10 bg-cBlue-50 border-b border-gray-200'>
					<div className='py-6 w-full max-w-7xl mx-auto'>
						<h1 className='mb-2 font-display text-h2-head font-semibold text-gray-800'>
							International Reference Pricing
						</h1>
						<p className='mb-4 text-label text-gray-500'>
							IRP Module is an add-on for Price Monitor, an AI-powered system integrating over 100 global pricing and
							reimbursement data sources.
						</p>
					</div>
				</div>

				<div className='px-10 pt-2 pb-10 bg-white'>
					<div className='w-full max-w-xs mx-auto'>
						<div className='pt-4 pb-6 w-full max-w-2xl mx-auto text-center'>
							<h3 className='mb-2 font-display font-semibold text-h3 text-gray-600'>Filter</h3>

							<div className='relative max-w-lg mb-2 mx-auto search'>
								<div className='flex items-stretch space-x-2'>
									<div className='w-full rounded border-2 border-gray-200 focus-within:border-cPurple-100 focus-within:ring-2 focus-within:ring-cPurple-50 transition-all'>
										<select
											name=''
											className={`w-full h-full px-2.5 py-3 font-display font-semibold ${
												selected ? 'text-cPurple-600' : 'text-gray-300'
											} border-0 rounded-l text-md outline-none focus:outline-none bg-white cursor-pointer`}
											defaultValue={'filter-brand-empty'}
											onChange={(e) => setSelected(e.target.value)}
										>
											<option value=''>Show all</option>
											<optgroup label='----------'></optgroup>

											{unique.map((opt) => (
												<option value={opt} key={opt}>
													{opt}
												</option>
											))}
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className='w-full max-w-2xl mx-auto'>
						<div className='mb-2 text-center'>
							<h3 className='font-display font-semibold text-h3 text-gray-600'>List</h3>
						</div>
						<div className='rounded bg-white border border-gray-200'>
							<div className='px-2 py-1 divide-y divide-slate-700'>
								{!sessions ||
									(sessions.length < 1 && (
										<h2>
											To see data here - please create session using Analytics or contact COGVIO Sales team for more
											information.
										</h2>
									))}
								{sessions &&
									(selected !== '' ? sessions.filter((x) => x.erpPack.brandName === selected) : sessions).map(
										(item) => {
											return (
												<div className='py-1' key={item.id} style={{ borderColor: 'rgb(228,228,231)' }}>
													<a
														href={`/irp/pack/${item.erpPack.id}/session/${item.id}`}
														className='flex flex-row flex-nowrap justify-between items-center w-full pl-1 pr-1 py-0.5 font-display tracking-wide rounded transition group hover:bg-gray-100'
													>
														<span className='grow-0 px-1.5 py-0.5 font-open text-label font-bold leading-5 text-gray-600 bg-gray-100 rounded whitespace-nowrap tabular-nums'>
															{formatHumanDateTime(new Date(item.created), undefined, 'numeric')}
														</span>
														<h4 className='grow-0 px-2 text-md leading-5 font-semibold text-cPurple-800 whitespace-nowrap'>
															{item.erpPack.virtualProductPackName.split('-')[0]}
														</h4>
														<p className='grow shrink w-full pr-2 text-md leading-5 font-semibold text-cPurple-500 truncate'>
															{item.erpPack.virtualProductPackName.split('-')[1]}
														</p>
														<span className='grow-0'>
															<span className='flex items-center justify-center w-4 h-4 rounded-full bg-gray-100'>
																<svg
																	xmlns='http://www.w3.org/2000/svg'
																	className='h-3 w-3'
																	fill='none'
																	viewBox='0 0 24 24'
																	stroke='currentColor'
																>
																	<path
																		strokeLinecap='round'
																		strokeLinejoin='round'
																		strokeWidth='2'
																		d='M9 5l7 7-7 7'
																	></path>
																</svg>
															</span>
														</span>
													</a>
												</div>
											);
										}
									)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</TailwindLayout>
	);
};
